import { FunctionComponent, useRef } from 'react';
import { TextLink } from '@spotify-internal/encore-web/core';
import { useT } from '@spotify-internal/i18n-core';
import { TextLinkToExternal } from '@components/shared/TextLinkToExternal';
import { translateBlockedReason } from '@helpers/utils';
import {
  AlbumMetadata,
  AlbumMetadataArtist,
} from '@generated/grpc/spotify/artistwatchlist/v1';
import { HeldReleasesTableRowPopover } from './HeldReleasesTableRowPopover';

type ReasonTableCellProps = {
  row: AlbumMetadata;
};

export const ReasonTableCell: FunctionComponent<
  ReasonTableCellProps
> = props => {
  const { row } = props;

  const t = useT();
  const artistWatchlistTriggerRef = useRef<HTMLElement | null>(null);
  const textLinkToExternalRef = useRef<HTMLAnchorElement | null>(null);

  const HeldInReviewTooltipMessage = ({
    artist,
  }: {
    artist?: AlbumMetadataArtist | null;
  }) => {
    if (!artist) {
      return (
        <>
          {t(
            'atlas.held_releases.tooltip_message_no_artist',
            `This product is held in review because one or more artist are on the Artist Watchlist.`,
            'Indicates the release is held in review',
          )}
        </>
      );
    }

    const artistName = artist.artistName;
    const artistUri = (
      <TextLinkToExternal
        ref={textLinkToExternalRef}
        href={artist.artistUri}
        key={artist.artistUri}
        valueText={artist.artistUri!}
        valueTextColor="textBrightAccent"
        hasTextUnderline={false}
        handleClick={undefined}
        aria-label="artistUri-link"
      />
    );

    return (
      <>
        {t(
          'atlas.held_releases.tooltip_message',
          `This product is held in review because {artistName} {artistUri} is on the Artist Watchlist.`,
          'Indicates the release is held in review',
          { artistName, artistUri },
        )}
      </>
    );
  };

  return (
    <HeldReleasesTableRowPopover
      id="blocked-reason-tooltip"
      lastBtnRef={textLinkToExternalRef}
      popoverTriggerRef={artistWatchlistTriggerRef}
      popoverProps={{
        'aria-label': t(
          'atlas.held_releases.held_release_reason_popover_aria_label',
          'Modal describing why a release has been held',
          'Aria-label description of the modal displayed to explain why a release was held',
        ),
      }}
      overlay={
        <>
          <p>
            <HeldInReviewTooltipMessage artist={row.blockingArtist} />
          </p>
          <p>
            {t(
              'atlas.held_releases.action_message',
              'Request approval for the product. Tell us which rights you have for it or if this product is not a release from the Watchlist artist. Tell us as soon as you can. Verifying approvals takes up to 5 business days and may delay a product going live on time.',
              'Message to suggest actions that can be taken for on held releases ',
            )}
          </p>
        </>
      }
    >
      <TextLink
        ref={artistWatchlistTriggerRef}
        component="button"
        semanticColor="textBrightAccent"
      >
        {row.blockedReason
          ? translateBlockedReason(row.blockedReason, t)
          : row.blockedReason}
      </TextLink>
    </HeldReleasesTableRowPopover>
  );
};
